'use client'

import { ProTrialStatus } from '@/features/billing/pro-trial-status'

import type { CSSProperties, ReactNode } from 'react'
import { Suspense, lazy, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { AppHeader } from '@/components/layout/app-header'
import { BottomNav } from '@/components/layout/bottom-nav'
import { DesktopSidebar } from '@/components/layout/desktop-sidebar'
// HAB-54: KaizenReviewModal lazy-loaded — only opens once per week per user,
// so keeping its 300+ lines (FormModal + Habit loaders) out of the eager AppShell paint.
const KaizenReviewModal = lazy(() =>
  import('@/components/feedback/kaizen-review-modal').then(m => ({ default: m.KaizenReviewModal })),
)
import { OfflineSyncManager } from '@/components/feedback/offline-sync-manager'
import { MissionFanfare } from '@/features/missions/components/missions/mission-fanfare'
import { OfflineBanner } from '@/components/ui/offline-banner'
import { RouteSpinner } from '@/components/ui/route-spinner'
import { ThemeLoader } from '@/components/feedback/theme-loader'
import { ContentScreenSkeleton } from '@/components/feedback/skeletons/screen-skeletons'
import { useAccessibility } from '@/state/session/context'
import { useTheme } from '@/state/theme'
import { useRouteChangeNotifications } from '@/hooks/use-route-change-notifications'
import { cn } from '@/lib/utils'

interface AppShellProps {
  children: ReactNode
}

export function AppShell({ children }: AppShellProps): React.JSX.Element {
  const pathname = usePathname()
  const sessionAccessibility = useAccessibility()
  const { isThemeChanging, isPreviewActive, stopPreview } = useTheme()
  useRouteChangeNotifications()
  const accessibility =
    sessionAccessibility ??
    ({ reduceMotion: false, highContrast: false, fontScale: 'normal' } as const)
  const accessibilityStyle: CSSProperties = {
    fontSize: accessibility.fontScale === 'grande' ? '1.04rem' : undefined,
  }

  useEffect(() => {
    if (!isPreviewActive) return
    if (pathname.startsWith('/emporium')) return
    stopPreview()
  }, [pathname, isPreviewActive, stopPreview])

  return (
    <div
      data-habitpack-shell
      data-high-contrast={accessibility.highContrast}
      aria-busy={isThemeChanging}
      className={cn(
        "flex min-h-dvh flex-col bg-background text-foreground transition-colors duration-500",
        accessibility.reduceMotion && "!duration-0 !animate-none [&_*]:!duration-0 [&_*]:!animate-none"
      )}
      style={accessibilityStyle}
    >
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:bg-background focus:text-foreground focus:px-4 focus:py-2 focus:rounded-md">Skip to main content</a>
      <ThemeLoader />
      <OfflineBanner />
      <OfflineSyncManager />
      <AppHeader />

      <div className="flex min-h-0 flex-1">
        <DesktopSidebar />

        {/* Bottom padding: 96px (pb-24) on mobile accounts for the fixed BottomNav height (48px item + 12px safe-area + padding).
            If BottomNav height changes, update this value. See bottom-nav.tsx. */}
        <main
          id="main-content"
          className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-24 lg:pb-6"
          tabIndex={-1}
        >
          <div className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-4 sm:px-5 md:px-6 lg:px-6">
            {pathname === '/settings/subscription' ? null : <ProTrialStatus compact />}
            <Suspense fallback={<ContentScreenSkeleton />}>
              {children}
            </Suspense>
          </div>
        </main>
      </div>

      <BottomNav />
      <Suspense fallback={null}>
        <KaizenReviewModal />
      </Suspense>
      <MissionFanfare />
    </div>
  )
}
